import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  companyProfileApiClient,
  ProfileUpdateErrorResponse,
  VerifyEmailErrorResponse,
} from '@src/api/company/profile/companyProfileApiClient';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { fetchCompanyUserProfile } from '@src/context/auth/ProfileFetcher';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { TogglePassword } from '@src/features/company/auth/components/TogglePassword';
import { useToast } from '@src/features/toast/context/toastContext';
import { useScrollToError } from '@src/hooks/useScrollToError';
import { useUnsavedChanges } from '@src/hooks/useUnsavedChange';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import AvatarDescription from '@src/ui/elements/avatar/AvatarDescription';
import AvatarInput from '@src/ui/elements/avatar/AvatarInput';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage, Textarea } from '@src/ui/elements/form';
import Checkbox from '@src/ui/elements/form/Checkbox';
import Input from '@src/ui/elements/form/Input';
import ToggleSwitch from '@src/ui/elements/form/ToggleSwitch';
import Title from '@src/ui/elements/typography/Title';
import ConfirmLeaveModal from '@src/ui/templates/Shared/ConfirmLeaveModal';
import VerifyEmailModal from '@src/ui/templates/Shared/VerifyEmailModal';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import logger from '@src/utils/logger';
import React, { useCallback, useEffect, useState } from 'react';

type Company = {
  name: string;
  industry_text: string;
};

type Tenant = {
  service: {
    enterprise_portal: boolean;
    dx_diagnosis: boolean;
    online_community: boolean;
  };
};

type FormValues = {
  familyName: string;
  firstName: string;
  familyNameKana: string;
  firstNameKana: string;
  birthday: string;
  email: string;
  avatarUrl: string;
  newPassword: string;
  newPasswordConfirmation: string;
  loggedInAt: string | null;
  displayName: string;
  affiliation: string;
  position: string;
  introduction: string;
  industryVisible: boolean;
  twoFactorEnabled: boolean;
  emailNotificationEnabled: boolean;
};

export type VerifyFormValues = {
  code: string;
  password: string;
};

const ProfileForm: React.FC = () => {
  const [formValues, setFormValues] = useState<FormValues>({
    familyName: '',
    firstName: '',
    familyNameKana: '',
    firstNameKana: '',
    birthday: '',
    email: '',
    avatarUrl: '',
    newPassword: '',
    newPasswordConfirmation: '',
    loggedInAt: '',
    displayName: '',
    affiliation: '',
    position: '',
    introduction: '',
    industryVisible: true,
    twoFactorEnabled: false,
    emailNotificationEnabled: true,
  });
  const [initialFormValues, setInitialFormValues] = useState<FormValues | null>(null);
  const [errors, setErrors] = useState<ProfileUpdateErrorResponse>();
  const [verifyValues, setVerifyValues] = useState<VerifyFormValues>({
    code: '',
    password: '',
  });
  const [verifyErrors, setVerifyErrors] = useState<VerifyEmailErrorResponse>();
  const toast = useToast();
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [originalEmail, setOriginalEmail] = useState<string>('');
  const [company, setCompany] = useState<Company | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const companyAuth = useCompanyAuth();

  const [emailVerificationCancelled, setEmailVerificationCancelled] = useState<boolean>(false);
  const [pendingEmailChange, setPendingEmailChange] = useState<string>('');

  const [isRevealNewPassword, setIsRevealNewPassword] = useState(false);
  const [isRevealNewPasswordConfirmation, setIsRevealNewPasswordConfirmation] = useState(false);

  const toggleNewPassword = () => {
    setIsRevealNewPassword((prevState) => !prevState);
  };

  const toggleNewPasswordConfirmation = () => {
    setIsRevealNewPasswordConfirmation((prevState) => !prevState);
  };

  const closeModal = () => {
    setIsOpen(false);
    setVerifyValues({
      code: '',
      password: '',
    });
    setVerifyErrors(undefined);

    if (formValues.email !== originalEmail) {
      setEmailVerificationCancelled(false);
      setPendingEmailChange('');
      setFormValues((prev) => ({ ...prev, email: originalEmail }));
    }
  };

  const hasUnsavedChanges = useCallback(() => {
    if (!initialFormValues) {
      return false;
    }

    return Object.keys(formValues).some((key) => {
      const currentValue = formValues[key as keyof FormValues];
      const initialValue = initialFormValues[key as keyof FormValues];
      return currentValue !== initialValue;
    });
  }, [formValues, initialFormValues]);

  const { isModalOpen, confirmReturn, cancelReturn } = useUnsavedChanges({
    hasUnsavedChanges: hasUnsavedChanges() || isOpen,
    onConfirm: () => {
      if (isOpen) {
        closeModal();
      } else {
        window.history.back();
      }
    },
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const res = await companyProfileApiClient.show();
      handleApiResponse(res, (data) => {
        const email = data.user?.email || '';
        const profileData = {
          familyName: data.user?.family_name || '',
          firstName: data.user?.first_name || '',
          familyNameKana: data.user?.family_name_kana || '',
          firstNameKana: data.user?.first_name_kana || '',
          birthday: data.user?.birthday || '',
          email: email,
          avatarUrl: data.user?.avatar_url || '',
          newPassword: '',
          newPasswordConfirmation: '',
          loggedInAt: data.user?.logged_in_at || '',
          displayName: data.user?.display_name || '',
          affiliation: data.user?.affiliation || '',
          position: data.user?.position || '',
          introduction: data.user?.introduction || '',
          industryVisible: data.user?.industry_visible ?? true,
          twoFactorEnabled: data.user?.two_factor_enabled ?? false,
          emailNotificationEnabled: data.user?.email_notification_enabled ?? true,
        };

        setFormValues(profileData);
        setInitialFormValues(profileData);
        setOriginalEmail(email);
        setCompany(data.company || null);
        setTenant(data.tenant || null);
      });
    };
    fetchProfile();
  }, []);

  const handleImageChange = async (file: File | null) => {
    if (!file) {
      formValues.avatarUrl = '';
      return;
    }
    setLoading(true);
    const res = await companyProfileApiClient.uploadAvatar(file);
    handleApiResponse(
      res,
      (data) => {
        formValues.avatarUrl = data.url;
      },
      (errors) => {
        alert(firstError(errors.file));
      },
    );
    setLoading(false);
  };

  const onSave = async () => {
    setLoading(true);
    setErrors(undefined);

    const emailChanged =
      formValues.email !== originalEmail ||
      (emailVerificationCancelled && pendingEmailChange !== originalEmail);

    const requestData = {
      family_name: formValues.familyName,
      first_name: formValues.firstName,
      family_name_kana: formValues.familyNameKana,
      first_name_kana: formValues.firstNameKana,
      birthday: formValues.birthday,
      avatar_url: formValues.avatarUrl,
      new_password: formValues.newPassword,
      new_password_confirmation: formValues.newPasswordConfirmation,
      display_name: formValues.displayName,
      affiliation: formValues.affiliation,
      position: formValues.position,
      introduction: formValues.introduction,
      industry_visible: formValues.industryVisible,
      two_factor_enabled: formValues.twoFactorEnabled,
      email_notification_enabled: formValues.emailNotificationEnabled,
    };

    const res = emailChanged
      ? await companyProfileApiClient.preValidate({
          company_user: {
            ...requestData,
            email: emailVerificationCancelled ? pendingEmailChange : formValues.email,
          },
        })
      : await companyProfileApiClient.update({
          company_user: requestData,
        });

    handleApiResponse(
      res,
      () => {
        fetchCompanyUserProfile(companyAuth.setProfile);

        if (emailChanged) {
          if (emailVerificationCancelled) {
            setFormValues((prev) => ({ ...prev, email: pendingEmailChange }));
            setEmailVerificationCancelled(false);
          }
          setInitialFormValues({
            ...formValues,
            email: emailVerificationCancelled ? pendingEmailChange : formValues.email,
          });
          setIsOpen(true);
          return;
        }

        setFormValues((prev) => ({
          ...prev,
          newPassword: '',
          newPasswordConfirmation: '',
        }));

        setInitialFormValues({ ...formValues });
        toast(TOAST_MESSAGE.UPDATE);
      },
      (error) => {
        setErrors(error);
      },
    );
    setLoading(false);
  };

  const handleVerifyValuesChange = (newValues: Partial<VerifyFormValues>) => {
    setVerifyValues((prev) => ({ ...prev, ...newValues }));
  };

  const onVerify = async () => {
    setLoading(true);

    const verifyRes = await companyProfileApiClient.verifyEmail({
      code: verifyValues.code,
      password: verifyValues.password,
    });

    handleApiResponse(
      verifyRes,
      async () => {
        setIsOpen(false);

        const res = await companyProfileApiClient.update({
          company_user: {
            family_name: formValues.familyName,
            first_name: formValues.firstName,
            family_name_kana: formValues.familyNameKana,
            first_name_kana: formValues.firstNameKana,
            birthday: formValues.birthday,
            avatar_url: formValues.avatarUrl,
            new_password: formValues.newPassword,
            new_password_confirmation: formValues.newPasswordConfirmation,
            display_name: formValues.displayName,
            affiliation: formValues.affiliation,
            position: formValues.position,
            introduction: formValues.introduction,
            industry_visible: formValues.industryVisible,
            two_factor_enabled: formValues.twoFactorEnabled,
            email_notification_enabled: formValues.emailNotificationEnabled,
          },
        });

        handleApiResponse(
          res,
          () => {
            fetchCompanyUserProfile(companyAuth.setProfile);
            setOriginalEmail(formValues.email);

            setEmailVerificationCancelled(false);
            setPendingEmailChange('');

            setFormValues((prev) => ({
              ...prev,
              newPassword: '',
              newPasswordConfirmation: '',
            }));
            setVerifyValues({ code: '', password: '' });
            setInitialFormValues({ ...formValues });
            setVerifyErrors(undefined);
            toast(TOAST_MESSAGE.UPDATE);
          },
          (errors) => {
            logger.log(errors);
          },
        );
      },
      (verifyErrors) => {
        logger.log(verifyErrors);
        setVerifyErrors(verifyErrors);
      },
    );
    setLoading(false);
  };

  const onClose = () => {
    if (hasUnsavedChanges()) {
      window.history.back();
    } else {
      closeModal();
    }
  };

  const fieldRefs = useScrollToError(errors ?? {});

  return (
    <main className=''>
      <SpinnerOverlay loading={loading} />
      <form
        className=''
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <div className='px-6 py-2 lg:pb-0 lg:pt-16'>
          <div className='pt-6 lg:pt-0'>
            <Title>マイプロフィール</Title>
          </div>
        </div>
        <div>
          <div className='mx-auto max-w-[848px] px-6 py-[60px] lg:py-5 lg:pb-16'>
            <div className='flex flex-col'>
              <div className='flex justify-between gap-4'></div>
            </div>
            <div className='flex flex-col gap-[26px]'>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3 font-medium'>
                  <label className=''>氏名 </label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-2'
                    ref={(el) => (fieldRefs.current['company_user.family_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='山田'
                      value={formValues.familyName}
                      onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                      error={hasError(errors?.company_user?.family_name)}
                    />
                    <ErrorMessage>{firstError(errors?.company_user?.family_name)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col gap-2'
                    ref={(el) => (fieldRefs.current['company_user.first_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='太郎'
                      value={formValues.firstName}
                      onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                      error={hasError(errors?.company_user?.first_name)}
                    />
                    <ErrorMessage>{firstError(errors?.company_user?.first_name)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3 font-medium'>
                  <label className=''>氏名（フリガナ）</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-2'
                    ref={(el) => (fieldRefs.current['company_user.family_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='ヤマダ'
                      value={formValues.familyNameKana}
                      onChange={(e) =>
                        setFormValues({ ...formValues, familyNameKana: e.target.value })
                      }
                      error={hasError(errors?.company_user?.family_name_kana)}
                    />
                    <ErrorMessage>
                      {firstError(errors?.company_user?.family_name_kana)}
                    </ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col gap-2'
                    ref={(el) => (fieldRefs.current['company_user.first_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='タロウ'
                      value={formValues.firstNameKana}
                      onChange={(e) =>
                        setFormValues({ ...formValues, firstNameKana: e.target.value })
                      }
                      error={hasError(errors?.company_user?.first_name_kana)}
                    />
                    <ErrorMessage>{firstError(errors?.company_user?.first_name_kana)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3 font-medium'>
                  <label className=''>メールアドレス </label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-2'
                    ref={(el) => (fieldRefs.current['company_user.email'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='sample@sample.co.jp'
                      value={formValues.email}
                      onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
                      error={hasError(errors?.company_user?.email)}
                    />
                    <ErrorMessage>{firstError(errors?.company_user?.email)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3 font-medium'>
                  <label className=''>生年月日</label>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-[200px] flex-col gap-2'
                    ref={(el) => (fieldRefs.current['company_user.birthday'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='19900101'
                      value={formValues.birthday}
                      autoComplete='off'
                      onChange={(e) => setFormValues({ ...formValues, birthday: e.target.value })}
                      error={hasError(errors?.company_user?.birthday)}
                      inputMode='numeric'
                    />
                    <ErrorMessage>{firstError(errors?.company_user?.birthday)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3 font-medium'>
                  <label className=''>新しいパスワード</label>
                  <div className='grid place-items-center text-[10px]'>
                    ※入力があった場合のみ更新
                  </div>
                </div>
                <div className='flex gap-4 lg:flex-col'>
                  <div
                    className='flex w-full flex-col gap-2'
                    ref={(el) => (fieldRefs.current['new_password'] = el)}
                  >
                    <div className='relative'>
                      <Input
                        size='m'
                        type={isRevealNewPassword ? 'text' : 'password'}
                        placeholder='パスワード'
                        value={formValues.newPassword}
                        autoComplete='new-password'
                        onChange={(e) =>
                          setFormValues({
                            ...formValues,
                            newPassword: e.target.value,
                          })
                        }
                        error={hasError(errors?.company_user?.new_password)}
                      />
                      <button
                        type='button'
                        onClick={toggleNewPassword}
                        className='toggle-password'
                      >
                        <TogglePassword isReveal={isRevealNewPassword} />
                      </button>
                    </div>
                    <ErrorMessage>{firstError(errors?.company_user?.new_password)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col gap-2'
                    ref={(el) => (fieldRefs.current['new_password_confirmation'] = el)}
                  >
                    <div className='relative'>
                      <Input
                        size='m'
                        type={isRevealNewPasswordConfirmation ? 'text' : 'password'}
                        placeholder='パスワード（確認用）'
                        value={formValues.newPasswordConfirmation}
                        autoComplete='new-password'
                        onChange={(e) =>
                          setFormValues({
                            ...formValues,
                            newPasswordConfirmation: e.target.value,
                          })
                        }
                        error={hasError(errors?.company_user?.new_password_confirmation)}
                      />
                      <button
                        type='button'
                        onClick={toggleNewPasswordConfirmation}
                        className='toggle-password'
                      >
                        <TogglePassword isReveal={isRevealNewPasswordConfirmation} />
                      </button>
                    </div>
                    <ErrorMessage>
                      {firstError(errors?.company_user?.new_password_confirmation)}
                    </ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-1'>
                <div className='flex items-center'>
                  <span className='mr-6 text-[16px] font-medium'>最終ログイン日時：</span>
                  <div className='flex gap-1'>
                    <span className='text-[16px]'>{formValues.loggedInAt ?? ''}</span>
                  </div>
                </div>
              </div>
              <div className='flex items-center gap-3 font-medium'>
                <label className=''>表示アバター</label>
              </div>
              <div className='flex flex-col gap-4'>
                <div
                  className='flex w-full gap-6 lg:flex-col'
                  ref={(el) => (fieldRefs.current['company_user.avatar_url'] = el)}
                >
                  <div className='flex-[0_0_auto]'>
                    <AvatarInput
                      onImageChange={handleImageChange}
                      url={formValues.avatarUrl}
                      error={firstError(errors?.company_user?.avatar_url)}
                    />
                  </div>
                  <div className='w-full'>
                    <AvatarDescription />
                  </div>
                </div>
              </div>
              <div className='mt-8 flex flex-col gap-2'>
                <div className='flex items-center gap-10'>
                  <span className='text-lg font-medium'>2段階認証</span>
                  <ToggleSwitch
                    checked={formValues.twoFactorEnabled}
                    onChange={(checked) =>
                      setFormValues((prev) => ({ ...prev, twoFactorEnabled: checked }))
                    }
                  />
                </div>
                <div ref={(el) => (fieldRefs.current['company_user.two_factor_enabled'] = el)}>
                  <ErrorMessage>
                    {firstError(errors?.company_user?.two_factor_enabled)}
                  </ErrorMessage>
                </div>
              </div>
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-10'>
                  <span className='text-lg font-medium'>メールでのお知らせ</span>
                  <ToggleSwitch
                    checked={formValues.emailNotificationEnabled}
                    onChange={(checked) =>
                      setFormValues((prev) => ({ ...prev, emailNotificationEnabled: checked }))
                    }
                  />
                </div>
                <div
                  ref={(el) => (fieldRefs.current['company_user.email_notification_enabled'] = el)}
                >
                  <ErrorMessage>
                    {firstError(errors?.company_user?.email_notification_enabled)}
                  </ErrorMessage>
                </div>
              </div>
            </div>
          </div>
        </div>
        {tenant?.service?.online_community && (
          <>
            <div className='px-6 py-2 lg:pb-0 lg:pt-16'>
              <div className='pt-6 lg:pt-0'>
                <Title>コミュニティプロフィール</Title>
              </div>
            </div>
            <div>
              <div className='mx-auto max-w-[848px] px-6 py-[60px] lg:py-5 lg:pb-16'>
                <div className='flex flex-col gap-[26px]'>
                  <div className='flex flex-col gap-[6px]'>
                    <div className='flex items-center gap-3 font-medium'>
                      <label>表示名</label>
                      <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                        必須
                      </div>
                    </div>
                    <div className='flex gap-4'>
                      <div
                        className='flex w-full flex-col gap-2'
                        ref={(el) => (fieldRefs.current['company_user.display_name'] = el)}
                      >
                        <Input
                          size='m'
                          type='text'
                          placeholder='山田太郎／DX担当ヤマダ／タロウ（事業承継中）'
                          value={formValues.displayName}
                          onChange={(e) =>
                            setFormValues({ ...formValues, displayName: e.target.value })
                          }
                          error={hasError(errors?.company_user?.display_name)}
                        />
                        <ErrorMessage>
                          {firstError(errors?.company_user?.display_name)}
                        </ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col gap-1'>
                    <div className='flex items-center'>
                      <span className='mr-8 text-[16px] font-medium leading-[150%] tracking-normal'>
                        企業名
                      </span>
                    </div>
                    <div className='text-[16px] font-normal leading-[150%] tracking-normal'>
                      {company?.name}
                    </div>
                  </div>
                  <div className='flex flex-col gap-[6px]'>
                    <div className='flex items-center gap-3 font-medium'>
                      <label>所属</label>
                    </div>
                    <div className='flex gap-4'>
                      <div
                        className='flex w-full flex-col gap-2'
                        ref={(el) => (fieldRefs.current['company_user.affiliation'] = el)}
                      >
                        <Input
                          size='m'
                          type='text'
                          placeholder='企画開発部／経営企画室／営業部'
                          value={formValues.affiliation}
                          onChange={(e) =>
                            setFormValues({ ...formValues, affiliation: e.target.value })
                          }
                          error={hasError(errors?.company_user?.affiliation)}
                        />
                        <ErrorMessage>{firstError(errors?.company_user?.affiliation)}</ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col gap-[6px]'>
                    <div className='flex items-center gap-3 font-medium'>
                      <label>役職</label>
                    </div>
                    <div className='flex gap-4'>
                      <div
                        className='flex w-full flex-col gap-2'
                        ref={(el) => (fieldRefs.current['company_user.position'] = el)}
                      >
                        <Input
                          size='m'
                          type='text'
                          placeholder='営業部 部長／製造部 技術スタッフ'
                          value={formValues.position}
                          onChange={(e) =>
                            setFormValues({ ...formValues, position: e.target.value })
                          }
                          error={hasError(errors?.company_user?.position)}
                        />
                        <ErrorMessage>{firstError(errors?.company_user?.position)}</ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col gap-1'>
                    <div className='flex items-center'>
                      <span className='mr-8 text-[16px] font-medium leading-[150%] tracking-normal'>
                        業種
                      </span>
                      <div className='flex items-center gap-2'>
                        <span className='text-[10px] font-semibold leading-[25.65px] tracking-normal'>
                          表示
                        </span>
                        <Checkbox
                          checked={formValues.industryVisible}
                          onChange={(e) =>
                            setFormValues({
                              ...formValues,
                              industryVisible: e.target.checked,
                            })
                          }
                          className='size-4'
                        />
                        <span className='text-[10px] font-normal leading-[25.65px] tracking-normal'>
                          ※非表示にする場合はチェックを外す
                        </span>
                      </div>
                    </div>
                    <div className='text-[16px] font-normal leading-[150%] tracking-normal'>
                      {company?.industry_text}
                    </div>
                  </div>
                  <div className='flex flex-col gap-[6px]'>
                    <div className='flex items-center gap-3 font-medium'>
                      <label>自己紹介</label>
                    </div>
                    <div className='flex gap-4'>
                      <div
                        className='flex w-full flex-col gap-2'
                        ref={(el) => (fieldRefs.current['company_user.introduction'] = el)}
                      >
                        <Textarea
                          value={formValues.introduction}
                          height='115px'
                          placeholder='○○市で印刷業を営んでいます。現在はDXを進めており、似た課題に取り組んでいる方と情報交換したいです。事業承継の相談もしたいと思っています。'
                          onChange={(value) =>
                            setFormValues({ ...formValues, introduction: value })
                          }
                          error={hasError(errors?.company_user?.introduction)}
                        />
                        <div className='flex items-center justify-between'>
                          <ErrorMessage>
                            {firstError(errors?.company_user?.introduction)}
                          </ErrorMessage>
                          <div className='text-sm'>
                            <span
                              className={formValues.introduction.length > 500 ? 'text-red-700' : ''}
                            >
                              {formValues.introduction.length}
                            </span>
                            /500
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div className='mb-10 flex justify-center'>
          <div className='w-[172px] lg:w-[120px]'>
            <Button
              size='s'
              outline
              fullWidth
              fullRounded
              onClick={onSave}
            >
              保存する
            </Button>
          </div>
        </div>
      </form>

      <ConfirmLeaveModal
        isOpen={isModalOpen}
        onConfirm={confirmReturn}
        onClose={cancelReturn}
      />

      <VerifyEmailModal
        verifyValues={verifyValues}
        onVerifyValuesChange={handleVerifyValuesChange}
        onVerify={onVerify}
        onClose={onClose}
        isOpen={isOpen}
        errors={verifyErrors}
      />
    </main>
  );
};

export default ProfileForm;
